// EntityListView.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent, Typography } from "@mhome/ui";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import EntityFilter from "./entity-filter";
import EntityDetailsDialog from "./entity-dialog";

import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { toEntityTitleSubtitle } from "../common/string-helpers";

const EntityCard = ({ meta, entity, getComponentBgColor, onSelect }) => {
  const entityInfo = toEntityTitleSubtitle(entity.name, true);
  const displayTitle =
    typeof entityInfo === "object" ? entityInfo.title : entityInfo;
  const displaySubtitle =
    typeof entityInfo === "object"
      ? entityInfo.subtitle
      : meta.integrationNames[entity.integration] || entity.integration;

  return (
    <Card
      elevation={0}
      className="border-none cursor-pointer transition-all duration-200 select-none hover:bg-accent active:bg-accent/80"
      style={{
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
      }}
      onClick={() => onSelect(entity)}
    >
      <CardContent
        className="h-full flex flex-col"
        style={{
          padding: "16px",
          minHeight: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Entity Name - 2 lines max */}
        <div
          style={{
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: "1.4",
            minHeight: "39px",
            color: "hsl(var(--foreground))",
            fontSize: "0.875rem",
            fontWeight: 500,
            flexShrink: 0,
            marginBottom: "8px",
            wordBreak: "break-word",
            width: "100%",
          }}
        >
          {displayTitle}
        </div>
        {/* Entity ID/Subtitle - 1 line only */}
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "hsl(var(--muted-foreground))",
            fontSize: "0.75rem",
            lineHeight: "1.2",
            width: "100%",
            marginTop: "auto",
            flexShrink: 0,
          }}
        >
          {entity.id || displaySubtitle}
        </div>
      </CardContent>
    </Card>
  );
};

const EntityListView = ({ meta, entities, showFilter }) => {
  const [entityFilterText, setEntityFilterText] = useState(""); // Changed to useState
  const [entitySortBy, setEntitySortBy] = useState("alphabet");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  // Moved the filtering and sorting logic into a useMemo to optimize performance
  const sortedEntities = useMemo(() => {
    return entities
      .filter((entity) => {
        // Text filter
        const searchTerms = entityFilterText
          .toLowerCase()
          .split(" ")
          .filter((term) => term.length > 0);
        const searchableText = entity.name.toLowerCase();
        const matchesText = !entityFilterText
          ? true
          : searchTerms.every((term) => searchableText.includes(term));

        // Integration filter
        const matchesIntegration =
          selectedIntegrations.length === 0 ||
          selectedIntegrations.includes(entity.integration);

        // Capability filter
        const matchesCapability =
          selectedCapabilities.length === 0 ||
          (entity.capabilities &&
            selectedCapabilities.some((capability) =>
              entity.capabilities.includes(capability)
            ));

        return matchesText && matchesIntegration && matchesCapability;
      })
      .sort((a, b) => {
        if (entitySortBy === "alphabet") {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const aIsLetter = /^[a-zA-Z]/.test(aName);
          const bIsLetter = /^[a-zA-Z]/.test(bName);
          if (aIsLetter && !bIsLetter) return -1;
          if (!aIsLetter && bIsLetter) return 1;
          return aName.localeCompare(bName);
        }
        return 0;
      });
  }, [
    entities,
    entityFilterText,
    entitySortBy,
    selectedIntegrations,
    selectedCapabilities,
  ]);

  return (
    <div style={{ paddingLeft: "8px", paddingRight: "8px" }}>
      {showFilter && (
        <EntityFilter
          sortBy={entitySortBy}
          setSortBy={setEntitySortBy}
          setFilterText={setEntityFilterText}
          meta={meta}
          selectedIntegrations={selectedIntegrations}
          setSelectedIntegrations={setSelectedIntegrations}
          selectedCapabilities={selectedCapabilities}
          setSelectedCapabilities={setSelectedCapabilities}
          entities={entities}
        />
      )}
      <ResponsiveCardGrid spacing={1.5} breakpoints={{ xs: 2, sm: 3, md: 4 }}>
        {sortedEntities.map((entity) => (
          <EntityCard
            key={entity.id}
            meta={meta}
            entity={entity}
            getComponentBgColor={getComponentBgColor}
            onSelect={setSelectedEntity}
          />
        ))}
      </ResponsiveCardGrid>
      <EntityDetailsDialog
        meta={meta}
        open={Boolean(selectedEntity)}
        entity={selectedEntity}
        onClose={() => setSelectedEntity(null)}
      />
    </div>
  );
};

export default EntityListView;
